import { getUserAgent } from './env';

export enum OperatingSystem {
  iOS = 'iOS',
  macOS = 'macOS',
  Android = 'Android',
  Windows = 'Windows',
  Linux = 'Linux',
  Other = 'other',
}

const osMatchers: Array<[RegExp, OperatingSystem]> = [
  [/iPhone|iPad|iPod/, OperatingSystem.iOS],
  [/Android/, OperatingSystem.Android],
  [/OS\s*X/, OperatingSystem.macOS],
  [/Windows/, OperatingSystem.Windows],
  [/Linux/, OperatingSystem.Linux],
];

export const enum Platform {
  Mobile = 'mobile',
  PC = 'PC',
}

function osToPlatform(os: OperatingSystem): Platform {
  switch (os) {
    case OperatingSystem.iOS:
    case OperatingSystem.Android:
      return Platform.Mobile;
  }

  return Platform.PC;
}

export const enum Browser {
  Edge = 'Edge',
  Chrome = 'Chrome',
  Safari = 'Safari',
  Firefox = 'Firefox',
  FBMessengerAndroid = 'FB Messenger for Android',
  FBMessengerIos = 'FB Messenger for iOS',
  GoogleSearch = 'Google Search',
  Other = 'other',
}

const browserMatchers: Array<[RegExp, Browser]> = [
  [/Edge/, Browser.Edge], // Needs to be first because it includes both Chrome and Safari
  [/FB_IAB\/.+;/, Browser.FBMessengerAndroid], // Can include Chrome so needs to come before it
  [/Chrome|CriOS/, Browser.Chrome],
  [/GSA/, Browser.GoogleSearch],
  [/Firefox/, Browser.Firefox],
  [/Safari/, Browser.Safari],
  [/FBAN\/Messenger.*ForiOS/, Browser.FBMessengerIos],
];

function match<T>(ua: string, matchers: Array<[RegExp, T]>, alternative: T): T {
  for (const [matcher, value] of matchers) {
    if (matcher.test(ua)) {
      return value;
    }
  }

  return alternative;
}

export interface IPlatformParams {
  readonly os: OperatingSystem;
  readonly platform: Platform;
  readonly browser: Browser;
  readonly version: readonly number[];
}

const versionRegexp = (prefix: string) =>
  new RegExp(`(?:${prefix})/((?:\\d+\\.)*(?:\\d+))`);

export const BrowserVersionMatchers: { [K in Browser]: RegExp} = {
  [Browser.Edge]: versionRegexp('Edge'),
  [Browser.Chrome]: versionRegexp('Chrome|CriOS'),
  [Browser.Safari]: versionRegexp('Version'),
  [Browser.Firefox]: versionRegexp('Firefox'),
  [Browser.FBMessengerAndroid]: versionRegexp('FBAV'),
  [Browser.FBMessengerIos]: versionRegexp('FBAV'),
  [Browser.GoogleSearch]: versionRegexp('GSA'),
  [Browser.Other]: versionRegexp('Mozilla'),
};

const matchBrowserVersion = (browser: Browser, ua: string): number[] => {
  const result = BrowserVersionMatchers[browser].exec(ua);

  if (result) {
    return result[1].split('.').map((x) => parseInt(x, 10));
  } else {
    return [0];
  }
};

export default function userAgent(ua: string = getUserAgent()): IPlatformParams {
  const os = match(ua, osMatchers, OperatingSystem.Other);
  const browser = match(ua, browserMatchers, Browser.Other);
  const version = matchBrowserVersion(browser, ua);
  const platform = osToPlatform(os);

  return { platform, os, browser, version };
}
